import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Stack,
  Divider,
  IconButton,
  Box,
  Tooltip,
  Skeleton,
  CardHeader,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import {
  ZAvatar,
  OutlinedIcons,
  LabelAndValueElement,
  copyToClipboard,
  useApiClient,
  useFetchImmutable,
  useCurrentTime,
  formatPhoneNumber,
  formatTimeWithTimeZoneName,
  ContextMenuComplete,
  useFetch,
} from '@marageti/z4-lib';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { Partner, TripRequest } from '@marageti/z4-sdk/lib/travel';
import ReactCountryFlag from 'react-country-flag';
import { UserStatus } from '@marageti/z4-sdk/lib/auth';
import TravelHistoryModal, { SelectedTab, useTravelHistoryTripRequests, useTravelHistorySoldTrips } from '../travel-history-modal';
import { EditTravelerForm } from '../modals/edit-traveler-form';
import { EditTravelerEmailForm } from '../modals/edit-traveler-email-form';
import { useTravelHistoryReviews } from '../travel-history-modal/travel-history-modal-container';
import useTravelerTicketsCount from '../../hooks/use-traveler-tickets-count';
import { useJustCallDialer } from '../../hooks/use-justcall-dialer';
import { FEATURES, FeatureFlag } from '../../store/contexts/feature-flags';

const {
  OutlinedDocumentDuplicateIcon,
  OutlinedPhoneIcon,
} = OutlinedIcons;

type TravelerProps = {
  travelers: Traveler[];
  tripRequest: TripRequest | undefined;
  onEditProfile?: () => void;
  onEditEmail?: () => void;
};

type StatItemProps = {
  label: string;
  count: number | undefined;
  onClick?: () => void;
  to?: string;
};

const StatItem = ({
  label, count, onClick, to,
}: StatItemProps) => (
  <Stack
    alignItems="start"
    onClick={onClick}
    component={to ? Link : 'div'}
    to={to || undefined}
    sx={{
      cursor: onClick || to ? 'pointer' : 'default',
      textDecoration: 'none',
      '&:hover': onClick || to ? { textDecoration: 'underline' } : {},
      flex: 1,
    }}
  >
    <Typography variant="captionBold" color="primary">
      {count !== undefined ? count : '-'}
    </Typography>
    <Typography variant="captionBold" color="primary">
      {label}
    </Typography>
  </Stack>
);

const TravelerCardSkeleton = () => (
  <Card variant="loose">
    <Box sx={{ pt: 3, pl: 3 }} display="flex">
      <Skeleton variant="circular" width={40} height={40} />
    </Box>
    <Box sx={{ px: 3, pb: 2, pt: 1.5 }}>
      <Skeleton variant="text" width={150} height={32} />
      <Skeleton variant="text" width={100} height={20} />
    </Box>
    <Divider sx={{ mx: 3 }} />
    <CardContent>
      <Stack spacing={2}>
        <LabelAndValueElement
          title="Email"
          value={<Skeleton variant="text" width={200} height={24} />}
          key="email-skeleton"
        />
        <LabelAndValueElement
          title="Phone"
          value={<Skeleton variant="text" width={120} height={24} />}
          key="phone-skeleton"
        />
        <LabelAndValueElement
          title="Country of Residence"
          value={<Skeleton variant="text" width={120} height={24} />}
          key="country-skeleton"
        />
        <LabelAndValueElement
          title="Timezone"
          value={<Skeleton variant="text" width={120} height={24} />}
          key="timezone-skeleton"
        />
      </Stack>
    </CardContent>
  </Card>
);

const TravelerCard = ({
  travelers,
  tripRequest,
  onEditProfile,
  onEditEmail,
}: TravelerProps) => {
  const primaryTraveler = travelers[0];
  const apiClient = useApiClient();
  const {
    data: countryCodes,
  } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { data: partner } = useFetch<Partner>(apiClient.partnersClient.getPartner, [primaryTraveler?.referringPartnerId], !!primaryTraveler?.referringPartnerId);
  const navigate = useNavigate();
  const { tripRequests } = useTravelHistoryTripRequests(primaryTraveler?.id);
  const { soldTrips } = useTravelHistorySoldTrips(primaryTraveler?.id);
  const { reviews } = useTravelHistoryReviews(primaryTraveler?.id);
  const { ticketCount } = useTravelerTicketsCount(primaryTraveler?.id);
  const [isProfileEditOpen, setIsProfileEditOpen] = useState(false);
  const [isEmailEditOpen, setIsEmailEditOpen] = useState(false);
  const [isTravelHistoryModalOpen, setIsTravelHistoryModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<SelectedTab>('trip-requests');

  const phoneNumber = formatPhoneNumber(primaryTraveler?.phone?.prefix, primaryTraveler?.phone?.number);
  const [time] = useCurrentTime(primaryTraveler?.timeZone?.name);
  const {
    launchDialerWithNumber,
    isDialerLockBlocked,
    isDialerLockHeldRemotely,
  } = useJustCallDialer();

  const phoneDialerRemoteDisabled = isDialerLockHeldRemotely;
  let phoneDialerTooltipTitle = 'Start a Call/SMS';
  if (isDialerLockBlocked) {
    phoneDialerTooltipTitle = 'Phone active in another tab or window';
  } else if (isDialerLockHeldRemotely) {
    phoneDialerTooltipTitle = 'Dialer is open in another tab';
  }

  const onClickProfileEdit = () => {
    setIsProfileEditOpen(true);
  };

  const handleCloseProfileEdit = () => {
    setIsProfileEditOpen(false);
  };

  const onClickEmailEdit = () => {
    setIsEmailEditOpen(true);
  };

  const handleCloseEmailEdit = () => {
    setIsEmailEditOpen(false);
  };

  const onClickTravelHistoryModal = (initialTab: SelectedTab) => {
    setSelectedTab(initialTab);
    setIsTravelHistoryModalOpen(true);
  };

  const handleCloseTravelHistoryModal = () => {
    setIsTravelHistoryModalOpen(false);
  };

  const handleTabChange = (nextTab: SelectedTab) => {
    setSelectedTab(nextTab);
  };

  const onClickStartCall = () => {
    if (!phoneNumber) return;
    launchDialerWithNumber(phoneNumber);
  };

  if (!primaryTraveler) {
    return <TravelerCardSkeleton />;
  }

  const getTooltipText = (traveler: Traveler, index: number, isPending: boolean) => {
    const isPrimaryTraveler = index === 0;
    let suffix = '';
    if (!isPrimaryTraveler) {
      suffix = isPending ? ' (Pending Confirmation)' : ' (Co-Traveler)';
    }

    return `${traveler.givenName} ${traveler.familyName}${suffix}`;
  };

  return (
    <>
      <Card variant="loose">
        <CardHeader
          title={(
            <>
              <Box display="flex">
                {travelers.map((traveler, index) => {
                  const isPending = traveler.userInfo[0]?.status === UserStatus.PENDING;
                  const tooltipText = getTooltipText(traveler, index, isPending);
                  return (
                    <Tooltip key={traveler.id} title={tooltipText} placement="top">
                      <span>
                        <ZAvatar
                          size={40}
                          src={traveler.avatar}
                          name={traveler.fullName}
                          alt={traveler.fullName}
                          loading={!traveler}
                          onClick={() => {
                            navigate(`/travelers/${traveler.id}`);
                          }}
                          sx={{
                            '&:hover': {
                              cursor: 'pointer',
                            },
                            opacity: isPending ? 0.4 : 1,
                            boxSizing: 'content-box',
                            border: '2px solid #FFFFFF',
                            marginLeft: index > 0 ? '-8px' : 0,
                            zIndex: travelers.length - index,
                          }}
                        />
                      </span>
                    </Tooltip>
                  );
                })}
              </Box>
              <Link to={`/travelers/${primaryTraveler?.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Box sx={{ pt: 1.5, pb: 2 }}>
                  <Typography variant="h3" sx={{ '&:hover': { textDecoration: 'underline' } }}>{primaryTraveler?.fullName}</Typography>
                  <Typography variant="caption" color="primary">Primary Traveler</Typography>
                </Box>
              </Link>
            </>
          )}
          action={(
            <ContextMenuComplete
              id="edit-traveler"
              items={[
                { action: onClickProfileEdit, label: 'Edit Profile' },
                { action: onClickEmailEdit, label: 'Edit Email Address' },
              ]}
            />
          )}
        />
        <Divider sx={{ mx: '24px' }} />
        <CardContent>
          <Stack spacing={2}>
            <LabelAndValueElement
              title="Email"
              value={primaryTraveler.email}
              key={`email-${primaryTraveler.email}`}
            />
            <LabelAndValueElement
              title="Phone"
              value={(
                <Box component="span" display="flex" alignItems="center">
                  {phoneNumber ? (
                    <>
                      {phoneNumber}
                      <IconButton
                        size="small"
                        onClick={() => phoneNumber && copyToClipboard(phoneNumber)}
                        aria-label="copy phone number"
                      >
                        <OutlinedDocumentDuplicateIcon color="primary" fontSize="inherit" />
                      </IconButton>
                      <FeatureFlag feature={FEATURES.JUST_CALL_DIALER}>
                        <Tooltip placement="top" title={phoneDialerTooltipTitle}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={onClickStartCall}
                              disabled={phoneDialerRemoteDisabled}
                              aria-label={
                                isDialerLockHeldRemotely
                                  ? 'Phone — dialer active in another tab or window'
                                  : 'start a call'
                              }
                            >
                              <OutlinedPhoneIcon color="primary" fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </FeatureFlag>
                    </>
                  ) : '-'}
                </Box>
              )}
              key="primary-phone"
            />
            <LabelAndValueElement
              title="Country of Residence"
              value={countryCodes ? (
                <>
                  {countryCodes[primaryTraveler?.countryOfResidence] || '-'}
                  {' '}
                  {tripRequest?.submittedFrom && countryCodes[tripRequest?.submittedFrom] && (
                    <Tooltip title={`Proxy IP: ${countryCodes[tripRequest?.submittedFrom]}`}>
                      <span>
                        <ReactCountryFlag countryCode={tripRequest.submittedFrom} svg />
                      </span>
                    </Tooltip>
                  )}
                </>
              ) : '-'}
              key="country"
            />
            <LabelAndValueElement
              title="Timezone"
              value={primaryTraveler?.timeZone ? formatTimeWithTimeZoneName(primaryTraveler?.timeZone, time) : '-'}
              key="timezone"
            />
          </Stack>
        </CardContent>
        <CardActions>
          <Stack
            direction="row"
            divider={<Divider orientation="vertical" flexItem />}
            sx={{ width: '100%', gap: 1.5 }}
          >
            <StatItem label="Requests" count={tripRequests?.totalHits} onClick={() => onClickTravelHistoryModal('trip-requests')} />
            <StatItem label="Sold Trips" count={soldTrips?.totalHits} onClick={() => onClickTravelHistoryModal('sold-trips')} />
            <StatItem label="Reviews" count={reviews?.totalHits} to={`/travelers/${primaryTraveler?.id}/reviews`} />
            <StatItem label="Tickets" count={ticketCount} to={`/travelers/${primaryTraveler?.id}/tickets`} />
          </Stack>
        </CardActions>
      </Card>
      {primaryTraveler && (
        <>
          <EditTravelerForm
            referringPartner={partner}
            open={isProfileEditOpen}
            onClose={handleCloseProfileEdit}
            traveler={primaryTraveler}
            onSubmitCallback={() => {
              if (onEditProfile) onEditProfile();
            }}
          />
          <EditTravelerEmailForm
            open={isEmailEditOpen}
            onClose={handleCloseEmailEdit}
            traveler={primaryTraveler}
            onSubmitCallback={() => {
              if (onEditEmail) onEditEmail();
            }}
          />
          <TravelHistoryModal
            open={isTravelHistoryModalOpen}
            onClose={handleCloseTravelHistoryModal}
            traveler={primaryTraveler}
            selectedTab={selectedTab}
            onTabChange={handleTabChange}
          />
        </>
      )}
    </>
  );
};

export default TravelerCard;
